// src/pages/Login.js
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import { toast } from "react-toastify";
import { FaHome, FaEye, FaEyeSlash } from "react-icons/fa";
import "./newLogin.css";

/**
 * Stable per-browser device id to support "new device" detection on the backend.
 * - Stored in localStorage.
 * - Sent to API as X-Device-Id.
 */
const DEVICE_ID_KEY = "nnetego_device_id";

const getOrCreateDeviceId = () => {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);

    if (!id) {
      id =
        (window.crypto &&
          typeof window.crypto.randomUUID === "function" &&
          window.crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random()
          .toString(16)
          .slice(2)}`;

      localStorage.setItem(DEVICE_ID_KEY, id);
    }

    return id;
  } catch {
    // Storage blocked (private mode / strict policies)
    return `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random()
      .toString(16)
      .slice(2)}`;
  }
};

const isJwtLike = (t) => typeof t === "string" && t.split(".").length === 3 && t.length > 20;

const normalizeEmail = (v) => String(v || "").trim().toLowerCase();

const isValidEmail = (v) => {
  // Lightweight check; keep backend as source of truth
  const s = normalizeEmail(v);
  return s.length >= 5 && s.includes("@") && s.includes(".");
};

const parseRetryAfterSeconds = (error) => {
  const ra = error?.response?.headers?.["retry-after"];
  if (!ra) return null;
  const n = Number(ra);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { handleLogin } = useAuth();

  const [email, setEmail] = useState(normalizeEmail(location.state?.email || ""));
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [rateLimitMessage, setRateLimitMessage] = useState("");

  // cooldown for 429 responses
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const cooldownTimerRef = useRef(null);

  const deviceId = useMemo(() => getOrCreateDeviceId(), []);

  // Prevent double-submit even before React re-renders
  const submitLockRef = useRef(false);

  // Cancel in-flight request if needed
  const abortRef = useRef(null);

  // Guard state updates after unmount (prevents "blink/reload feel")
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      try {
        abortRef.current?.abort?.();
      } catch {
        // ignore
      }
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (location.state?.email) {
      setEmail(normalizeEmail(location.state.email));
    }
  }, [location.state]);

  const clearMessages = useCallback(() => {
    if (errorMessage) setErrorMessage("");
    if (rateLimitMessage) setRateLimitMessage("");
  }, [errorMessage, rateLimitMessage]);

  const startCooldown = useCallback((seconds) => {
    const s = Math.max(1, Number(seconds) || 60);

    setCooldownSeconds(s);
    setRateLimitMessage(`Too many attempts. Please wait ${s} seconds before trying again.`);

    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);

    cooldownTimerRef.current = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const validateInputs = useCallback(() => {
    const e = normalizeEmail(email);
    if (!e || !password) {
      setErrorMessage("Please enter both email and password.");
      return false;
    }
    if (!isValidEmail(e)) {
      setErrorMessage("Please enter a valid email address.");
      return false;
    }
    return true;
  }, [email, password]);

  const getRedirectPath = useCallback((role) => {
    switch (role) {
      case "SystemAdmin":
      case "Admin":
        return "/admin/dashboard";
      case "Manager":
        return "/manager/dashboard";
      case "Receptionist":
        return "/receptionist/dashboard";
      case "Housekeeper":
        return "/housekeeper/dashboard";
      case "Maintenance":
        return "/maintenance/dashboard";
      case "Owner":
      case "User":
        return "/hotels/dashboard";
      default:
        return "/hotels/dashboard";
    }
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Prevent double-submit
      if (submitLockRef.current) return;

      clearMessages();

      // Don't allow attempts during cooldown
      if (cooldownSeconds > 0) {
        setRateLimitMessage(`Please wait ${cooldownSeconds} seconds before trying again.`);
        return;
      }

      if (!validateInputs()) return;

      submitLockRef.current = true;
      setIsLoading(true);

      // Cancel any previous in-flight login request
      try {
        abortRef.current?.abort?.();
      } catch {
        // ignore
      }
      abortRef.current = new AbortController();

      const safeEmail = normalizeEmail(email);

      try {
        const response = await axiosInstance.post(
          "/Auth/login",
          { email: safeEmail, password },
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Device-Id": deviceId,
            },
            withCredentials: true,
            signal: abortRef.current.signal,
          }
        );

        const accessToken = response?.data?.accessToken;

        // Critical: prevent bad auth state like token=true or empty tokens
        if (response.status === 200 && isJwtLike(accessToken)) {
          handleLogin(response.data);

          const role =
            (Array.isArray(response.data.user?.role)
              ? response.data.user.role[0]
              : response.data.user?.role) || "User";

          // Optional: if you use "return to where you came from", support it here:
          const from = location.state?.from?.pathname;
          navigate(from || getRedirectPath(role), { replace: true });

          toast.success("Logged in successfully!");
          return;
        }

        setErrorMessage("Unexpected response from server. Please try again.");
      } catch (error) {
        // If we aborted the request, do nothing
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }

        console.error("Login error:", error);

        const status = error?.response?.status;
        const apiError = error?.response?.data?.error;
        const apiMessage = error?.response?.data?.message;

        if (apiError === "EmailNotConfirmed") {
          navigate("/email-confirmation-pending", {
            state: {
              email: safeEmail,
              message: "Please confirm your email address before logging in.",
            },
            replace: true,
          });
          return;
        }

        // Account locked
        if (status === 423) {
          setErrorMessage(apiMessage || "Your account is temporarily locked. Please try again later.");
          return;
        }

        // Rate limiting
        if (status === 429) {
          const retryAfter = parseRetryAfterSeconds(error);
          startCooldown(retryAfter || 60);
          return;
        }

        // Unauthorized (wrong credentials)
        if (status === 401) {
          setErrorMessage(apiMessage || "Invalid email or password.");
          return;
        }

        setErrorMessage(apiMessage || "Login failed. Please try again.");
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
        }
        submitLockRef.current = false;
      }
    },
    [
      abortRef,
      clearMessages,
      cooldownSeconds,
      deviceId,
      email,
      getRedirectPath,
      handleLogin,
      location.state,
      navigate,
      password,
      startCooldown,
      validateInputs,
    ]
  );

  const onEmailChange = (val) => {
    setEmail(normalizeEmail(val));
    clearMessages();
  };

  const onPasswordChange = (val) => {
    setPassword(val);
    clearMessages();
  };

  const isSubmitDisabled = isLoading || cooldownSeconds > 0;

  return (
    <div
      className="login-container"
      style={{
        backgroundImage: `url('/images/login-background.png')`,
        backgroundSize: "cover",
        backgroundPosition: "center center",
      }}
    >
      <div className="login-box">
        <header className="app-header">
          <h2 className="app-name">
            <span className="dot">N</span>netego HMS<span className="dot">.</span>
          </h2>
          <p className="app-tagline">Streamline Your Hotel Management.</p>
        </header>

        {errorMessage && (
          <div className="error-message" role="alert" aria-live="assertive">
            {errorMessage}
          </div>
        )}

        {rateLimitMessage && (
          <div className="rate-limit-message" role="alert" aria-live="polite">
            {rateLimitMessage}
            {cooldownSeconds > 0 ? ` (${cooldownSeconds}s)` : ""}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="input-group">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              disabled={isLoading}
              required
              autoComplete="username"
              inputMode="email"
              name="email"
              aria-label="Email"
            />
          </div>

          <div className="input-group password-group">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              disabled={isLoading}
              required
              autoComplete="current-password"
              name="password"
              aria-label="Password"
            />

            {/* Use a real button so it never triggers form submit unintentionally */}
            <button
              type="button"
              className="toggle-eye"
              aria-label={showPassword ? "Hide password" : "Show password"}
              onClick={() => setShowPassword((s) => !s)}
              disabled={isLoading}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          <button type="submit" disabled={isSubmitDisabled}>
            {isLoading ? "Logging in..." : cooldownSeconds > 0 ? `Wait ${cooldownSeconds}s` : "Login"}
          </button>
        </form>

        <div className="forgot-password-link">
          <Link to="/forgot-password">Forgot Password?</Link>
        </div>

        <div className="register-link">
          <p>
            Don't have an account? <Link to="/register">Register here</Link>
          </p>
        </div>

        <div className="back-home">
          <Link to="/" className="home-link">
            <FaHome /> Back to Home
          </Link>
        </div>

        <footer className="login-footer">
          {/* Prefer local asset during dev to avoid ORB/CORS issues */}
          <a href="https://lanesands.io" target="_blank" rel="noreferrer" aria-label="Nnetego website">
            <img
              src="/images/logo.png"
              onError={(e) => {
                // fallback to remote if local missing
                e.currentTarget.src = "https://lanesands.io/images/logo.png";
              }}
              alt="Nnetego logo"
              className="footer-logo"
              loading="lazy"
            />
          </a>
          <div className="copyright">© 2025 Nnetego. All rights reserved.</div>
        </footer>
      </div>
    </div>
  );
};

export default Login;
